import React, { useState, useRef } from "react"
import { ReportAPI } from "../../api/apiClient"
import { useUI } from "../../context/UIContext"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"

interface Row {
  component: string
  vendor: string
  onRoll: number
  date: string
}

export default function ManPowerStatementVendorWiseReport({
  projectId
}:{
  projectId:number
}){

  const { selectedProjectName } = useUI()

  const [qDate,setQDate] = useState("")
  const [endDate,setEndDate] = useState("")
  const [data,setData] = useState<Row[]>([])
  const [showReport,setShowReport] = useState(false)

  const [expanded,setExpanded] = useState<Record<string,boolean>>({})
  const [showDaily,setShowDaily] = useState(false)

  const reportRef = useRef<HTMLDivElement>(null)

  const formatDate = (dateStr:string) => {

    if(!dateStr) return ""

    const d = new Date(dateStr)

    const day = String(d.getDate()).padStart(2,"0")

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

    const month = months[d.getMonth()]

    const year = d.getFullYear()

    return `${day}-${month}-${year}`
  }

  const splitDate = (dateStr:string) => {

    const d = new Date(dateStr)

    const day = String(d.getDate()).padStart(2,"0")

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

    const month = months[d.getMonth()]

    const year = d.getFullYear()

    return {day,month,year}
  }

  const getDates = () => {

    const dates:string[] = []

    if(!qDate || !endDate) return dates

    let d = new Date(qDate)
    const end = new Date(endDate)

    while(d <= end){

      dates.push(d.toISOString())

      d.setDate(d.getDate()+1)
    }

    return dates
  }

  const dateColumns = getDates()

  const load = async()=>{

    const res = await ReportAPI.getManPowerStatementVendorWise({
      project:projectId,
      qDate,
      endDate
    })

    setData(res)

    setShowReport(true)
  }

  /* ---------- SUMMARY ---------- */

  const summary:any = {}

  data.forEach(r=>{

    if(!summary[r.vendor])
      summary[r.vendor] = {}

    if(!summary[r.vendor][r.component])
      summary[r.vendor][r.component] = 0

    summary[r.vendor][r.component] += r.onRoll
  })

  const vendors = Object.keys(summary).sort()

  const pivot:any = {}

  data.forEach(r=>{

    const d = formatDate(r.date)

    if(!pivot[r.vendor])
      pivot[r.vendor] = {}

    if(!pivot[r.vendor][r.component])
      pivot[r.vendor][r.component] = {}

    pivot[r.vendor][r.component][d] = r.onRoll
  })

  /* ---------- PDF EXPORT ---------- */

  const exportPDF = async () => {

    const datesPerPage = 13

    const pdf = new jsPDF("l","mm","a4")

    const pages = showDaily
      ? Math.ceil(dateColumns.length / datesPerPage)
      : 1

    for(let p=0;p<pages;p++){

      const start = p * datesPerPage
      const end = start + datesPerPage

      const visibleDates = showDaily
        ? dateColumns.slice(start,end)
        : []

      const isLastPage = p === pages - 1

      const temp = document.createElement("div")

      temp.style.width = "1400px"
      temp.style.padding = "30px"
      temp.style.background = "white"

      temp.innerHTML = `

      ${
        p === 0
        ? `
        <h2 style="font-weight:bold">
        ${selectedProjectName}: ManPower Vendor Wise Report
        </h2>

        <div style="margin-bottom:15px;font-weight:bold">
        From ${formatDate(qDate)} to ${formatDate(endDate)}
        </div>
        `
        : ""
      }

      <table border="1" style="border-collapse:collapse;width:100%;font-size:14px;text-align:center">

      <thead style="background:#4c5a6a;color:white">

      <tr>

      <th style="padding:8px;text-align:left">Resource</th>

      ${
        showDaily
        ? visibleDates.map(d=>`<th style="padding:8px">${formatDate(d)}</th>`).join("")
        : ""
      }

      ${isLastPage ? `<th style="padding:8px">Total</th>` : ""}

      </tr>

      </thead>

      <tbody>

      ${
        vendors.map(vendor=>{

          const components = summary[vendor]

          return `

          <tr style="background:#e5e7eb;font-weight:bold">

          <td colspan="${
            showDaily
            ? visibleDates.length + (isLastPage ? 2 : 1)
            : (isLastPage ? 2 : 1)
          }" style="text-align:left;padding:8px">

          ${vendor}

          </td>

          </tr>

          ${
            Object.keys(components).map(comp=>{

              return `

              <tr>

              <td style="text-align:left;padding:8px">${comp}</td>

              ${
                showDaily
                ? visibleDates.map(d=>{

                    const val = pivot[vendor]?.[comp]?.[formatDate(d)] ?? ""

                    return `<td style="padding:8px">${val}</td>`

                  }).join("")
                : ""
              }

              ${isLastPage ? `<td style="padding:8px;font-weight:bold">${components[comp].toFixed(2)}</td>` : ""}

              </tr>

              `

            }).join("")
          }

          `

        }).join("")
      }

      </tbody>

      </table>
      `

      document.body.appendChild(temp)

      const canvas = await html2canvas(temp,{scale:3})

      const img = canvas.toDataURL("image/png")

      const width = pdf.internal.pageSize.getWidth()
      const height = canvas.height * width / canvas.width

      if(p>0) pdf.addPage()

      pdf.addImage(img,"PNG",0,10,width,height)

      pdf.setFontSize(10)

      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      pdf.text(
        `Page ${p+1} of ${pages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      )

      document.body.removeChild(temp)
    }

    pdf.save("ManPowerVendorWiseReport.pdf")
  }

  return(

    <div className="px-6 py-6">

      {!showReport && (

        <div className="max-w-[400px]">

          <h2 className="text-xl font-semibold mb-6">
            ManPower Vendor Wise Report
          </h2>

          <div className="mb-4">
            <label className="block text-sm mb-1">From Date</label>
            <input
              type="date"
              value={qDate}
              onChange={e=>setQDate(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e=>setEndDate(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>

          <button
            onClick={load}
            className="bg-blue-600 text-white px-6 py-2 rounded"
          >
            Run Report
          </button>

        </div>

      )}

      {showReport && (

        <div>

          <div className="flex justify-end mb-4">
            <button
              onClick={exportPDF}
              className="bg-green-600 text-white px-6 py-2 rounded"
            >
              Export PDF
            </button>
          </div>

          <div ref={reportRef}>

            <div className="mb-6">

              <div className="flex items-center gap-4 text-lg">
                <span className="font-bold">
                  {selectedProjectName}: ManPower Vendor Wise Report
                </span>

                <span
                  className="cursor-pointer text-blue-600 font-bold"
                  onClick={()=>setShowDaily(!showDaily)}
                >
                  {showDaily ? "- Total" : "+ Total"}
                </span>

              </div>

              <div className="text-sm text-gray-600 font-bold">
                From {formatDate(qDate)} to {formatDate(endDate)}
              </div>

            </div>

            <div className="overflow-x-auto">

              <div className="min-w-max">

                <div className="flex bg-[#4c5a6a] text-white px-4 py-2 text-sm font-semibold">

                  <span className="w-48">Resource</span>

                  {showDaily && dateColumns.map(d=>{

                    const {day,month,year} = splitDate(d)

                    return(

                      <div key={d} className="w-16 text-center">
                        <div>{day}</div>
                        <div className="text-xs">{month}</div>
                        <div className="text-xs">{year}</div>
                      </div>

                    )

                  })}

                  <span className="w-20 text-right">Total</span>

                </div>

                {vendors.map(vendor=>{

                  const components = summary[vendor]

                  return(

                    <div key={vendor}>

                      <div className="bg-gray-300 px-4 py-2 font-semibold">
                        {vendor}
                      </div>

                      {Object.keys(components).map(comp=>{

                        const key = vendor+"_"+comp
                        const open = expanded[key]

                        return(

                          <div key={comp}>

                            <div
                              className="flex items-center px-6 py-2 text-sm border-b"
                              onClick={()=>setExpanded(prev=>({
                                ...prev,
                                [key]:!prev[key]
                              }))}
                            >

                              <span className="w-5 font-bold mr-2">
                                {open ? "-" : "+"}
                              </span>

                              <span className="w-40">{comp}</span>

                              {showDaily && dateColumns.map(d=>{

                                const val = pivot[vendor]?.[comp]?.[formatDate(d)] ?? ""

                                return(
                                  <span key={d} className="w-16 text-center">
                                    {val}
                                  </span>
                                )

                              })}

                              <span className="w-20 text-right">
                                {components[comp].toFixed(2)}
                              </span>

                            </div>

                          </div>

                        )

                      })}

                    </div>

                  )

                })}

              </div>

            </div>

          </div>

        </div>

      )}

    </div>

  )

}